<template>
  <section class="runAccount">
    <template v-if="account">
      <h3 class="heading">What this run did and why</h3>
      <p v-if="account.why" class="why">{{ account.why }}</p>
      <div v-if="account.evidence?.length" class="block">
        <div class="label">Based on</div>
        <ul class="list">
          <li v-for="(line, i) in account.evidence" :key="i">{{ line }}</li>
        </ul>
      </div>
      <div v-if="account.uncertain" class="block">
        <div class="label">Not sure about</div>
        <p class="text">{{ account.uncertain }}</p>
      </div>
    </template>

    <template v-if="receipt && hasReceipt">
      <h3 class="heading">What Raven recorded</h3>
      <div v-if="receipt.kept?.length" class="block">
        <div class="label">Kept ({{ receipt.kept.length }})</div>
        <ul class="list">
          <li v-for="p in receipt.kept" :key="p.email">{{ p.name || p.email }}: {{ p.reason }}</li>
        </ul>
      </div>
      <div v-if="receipt.excluded?.length" class="block">
        <div class="label">Excluded ({{ receipt.excluded.length }})</div>
        <ul class="list">
          <li v-for="p in receipt.excluded" :key="p.email">{{ p.name || p.email }}: {{ p.reason }}</li>
        </ul>
      </div>
      <div v-if="receipt.drafts?.length" class="block">
        <div class="label">Drafts ({{ receipt.drafts.length }})</div>
        <ul class="list">
          <li v-for="(d, i) in receipt.drafts" :key="i">{{ d.to }}: {{ d.subject }}</li>
        </ul>
      </div>
      <div v-if="receipt.changes?.length" class="block">
        <div class="label">Strategy changes ({{ receipt.changes.length }})</div>
        <ul class="list">
          <li v-for="(c, i) in receipt.changes" :key="i">{{ changeText(c) }}</li>
        </ul>
      </div>
      <div v-if="receipt.sub_agents?.length" class="block">
        <div class="label">Agents it started ({{ receipt.sub_agents.length }})</div>
        <ul class="list">
          <li v-for="a in receipt.sub_agents" :key="a.run_id">
            <router-link :to="`/agent-runs/${a.run_id}`">{{ agentLabel(a) }}</router-link>: {{ a.status }}
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface RunAccountData { why?: string; evidence?: string[]; uncertain?: string }
interface Person { name?: string; email?: string; reason?: string }
interface Change { kind: string; play?: string }
interface SubAgent { run_id: string; kind: string; status: string; name?: string | null }
interface Receipt {
  kept?: Person[]; excluded?: Person[]; drafts?: { to: string; subject: string }[]
  changes?: Change[]; sub_agents?: SubAgent[]
}

const props = defineProps<{ account?: RunAccountData | null; receipt?: Receipt | null }>()

const hasReceipt = computed(() => {
  const r = props.receipt
  return !!r && [r.kept, r.excluded, r.drafts, r.changes, r.sub_agents].some((list) => (list?.length || 0) > 0)
})

const CHANGE_VERB: Record<string, string> = { create_play: 'New play', edit_play: 'Change brief', retire_play: 'Retire play' }
const AGENT: Record<string, string> = { play: 'Play', writer: 'Writer', helper: 'Helper', strategy: 'Strategy' }

function changeText(c: Change): string {
  return `${CHANGE_VERB[c.kind] || c.kind}: ${c.play || ''}`
}

function agentLabel(a: SubAgent): string {
  const kind = AGENT[a.kind] || a.kind
  return a.name ? `${kind} ${a.name}` : kind
}
</script>

<style scoped>
.runAccount {
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  line-height: var(--lh-normal);
}
.heading {
  font-size: var(--fs-sm);
  font-weight: 600;
  margin: var(--space-3) 0 var(--space-2);
}
.heading:first-child { margin-top: 0; }
.why, .text { margin: 0 0 var(--space-2); }
.block { margin-bottom: var(--space-2); }
.label { color: var(--color-text-secondary); margin-bottom: var(--space-1); }
.list { margin: 0; padding-left: var(--space-4); }
</style>
